<template>
  <Modal
    :headline="$t('learnDialog.learnByTopic')"
    :save-btn-disabled="topic.length === 0"
    :save-btn-name="$t('app.start')"
    :show-modal="showModal"
    :show-full-screen="true"
    @save="startMode"
    @cancel="showModal = false">
    <v-row>
      <v-col cols="12">
        <v-select
          :items="vocabularyTypeList"
          item-text="type"
          v-model="vocabularyType"
          :label="$t('learnDialog.vocabulary')"
          :hint="$t('learnDialog.chooseVocabularyList')"
          persistent-hint
          @change="vocabularyTypeSelected"
          outlined />
      </v-col>
      <v-col cols="12">
        <v-select
          :disabled="!vocabularyType"
          :items="formattedLanguageSets"
          v-model="languageSet"
          :label="$t('learnDialog.language')"
          :hint="$t('learnDialog.chooseLanguage')"
          persistent-hint
          @change="languageSetSelected"
          outlined />
      </v-col>
      <v-col cols="12">
        <v-select
          :disabled="!languageSet"
          :items="topics"
          v-model="topic"
          :label="$t('learnDialog.topic')"
          :hint="$t('learnDialog.chooseTopic')"
          persistent-hint
          outlined />
      </v-col>
    </v-row>
    <div v-if="loginHint">
      <p style="color: red;">
        {{ $t('learnDialog.loginHint') }}
      </p>
      <v-btn
        color="green"
        outlined
        @click="$router.push(pathTo.login)">
        {{ $t('login.login') }}
      </v-btn>
    </div>
    <div slot="activator">
      <v-row>
        <v-col>
          <v-btn
            x-large
            ripple
            block
            color="blue"
            class="white--text"
            @click="selectMode('vocabulary')">
            {{ $t('learnDialog.learnVocabularies') }}
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            x-large
            ripple
            block
            color="orange"
            class="white--text"
            @click="selectMode('quiz')">
            {{ $t('learnDialog.languageQuiz') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </Modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Modal from '@components/modal';
import { pathTo } from '@/plugins/router/helper';
import getPublicVocabularyLanguageSets from '@/mixins/vocabularies/getPublicVocabularyLanguageSets';
import getMyVocabularyLanguageSets from '@/mixins/vocabularies/getMyVocabularyLanguageSets';
import getPublicVocabularyList from '@/mixins/vocabularies/getPublicVocabularyList';
import getMyVocabularyList from '@/mixins/vocabularies/getMyVocabularyList';

export default {
  name: 'LearnDialog',
  components: {
    Modal,
  },
  mixins: [
    getPublicVocabularyLanguageSets,
    getMyVocabularyLanguageSets,
    getPublicVocabularyList,
    getMyVocabularyList,
  ],
  data: () => ({
    showModal: false,
    topic: '',
    mode: '',
    pathTo,
    vocabularyList: [],
    vocabularyTypeList: [],
    vocabularyType: '',
    languageSets: [],
    languageSet: '',
  }),
  computed: {
    ...mapGetters('user', ['userIsAuthenticated']),

    baseLang() {
      return this.$lang.getCode(this.languageSet.split(' - ')[0]);
    },
    topics() {
      return this.vocabularyList.map((item) => item[this.baseLang]);
    },
    vocabularyListFromTopic() {
      return this.vocabularyList.filter((item) => item[this.baseLang] === this.topic)[0];
    },
    loginHint() {
      return !this.userIsAuthenticated && this.vocabularyType === this.$t('app.myVocabulary');
    },
    formattedLanguageSets() {
      return this.languageSets
        .map((item) => item.split('_'))
        .map((item) => [this.$lang.getNativeName(item[0]), this.$lang.getNativeName(item[1])].join(' - '));
    },
  },
  methods: {
    ...mapActions('current', ['setVocabularyScore', 'setQuizScore', 'setLanguageSet']),
    ...mapActions('vocabularies', ['setVocabularyList']),

    async vocabularyTypeSelected() {
      await this.getLanguageSets();
      this.languageSet = '';
      this.topic = '';
    },
    async languageSetSelected() {
      await this.getVocabularyListFromVocabularyType();
      this.topic = '';
    },
    async getLanguageSets() {
      let languageSets = [];

      if (this.vocabularyType === this.$t('app.public')) {
        languageSets = await this.getPublicVocabularyLanguageSets();
      }
      if (this.vocabularyType === this.$t('app.myVocabulary') && this.userIsAuthenticated) {
        languageSets = await this.getMyVocabularyLanguageSets();
      }

      this.languageSets = languageSets || [];
    },
    async getVocabularyListFromVocabularyType() {
      const unformatLanguageSet = await this.languageSet.split(' - ')
        .map((item) => this.$lang.getCode(item)).join('_');

      const langCodes = unformatLanguageSet.split('_');
      this.setLanguageSet({
        baseLanguage: langCodes[0],
        learningLanguage: langCodes[1],
      });

      if (this.vocabularyType === this.$t('app.public')) {
        this.vocabularyList = await this.getPublicVocabularyList(unformatLanguageSet);
      }

      if (this.vocabularyType === this.$t('app.myVocabulary') && this.userIsAuthenticated) {
        this.vocabularyList = await this.getMyVocabularyList(unformatLanguageSet);
      }
    },
    selectMode(mode) {
      this.showModal = true;
      this.mode = mode;
    },
    startMode() {
      this.showModal = false;
      this.setVocabularyScore(0);
      this.setQuizScore(0);
      this.setVocabularyList(this.vocabularyListFromTopic);
      this.$router.push(pathTo[this.mode]);
    },
  },
  async created() {
    this.vocabularyTypeList = [
      { type: this.$t('app.public') },
      { type: this.$t('app.myVocabulary') },
    ];
  },
};
</script>
